import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, MapPin, Clock, ShieldCheck, RotateCcw, PackageCheck, PackageX, AlertTriangle, Sparkles, MessageCircle } from 'lucide-react';
import { useSiteBrand } from '../components/BrandLogo';

const SHIPPING = [
  {
    id: 'local',
    icon: MapPin,
    tag: 'MISMO DÍA',
    accent: 'text-orange-400',
    ring: 'group-hover:border-orange-500/40',
    color: 'from-orange-500/20 to-transparent',
    title: 'Zona Metropolitana de Monterrey',
    price: '$99 MXN',
    priceNote: 'o gratis en compras +$1,500',
    bullets: [
      'Cobertura: Monterrey, San Pedro, San Nicolás, Apodaca, Guadalupe, Santa Catarina, Escobedo, Cadereyta y García.',
      'Pedidos antes de la 1:30 pm de lunes a viernes se entregan el mismo día.',
      'Empaque discreto, sin logos externos.',
    ],
  },
  {
    id: 'nacional',
    icon: Truck,
    tag: '1 a 3 DÍAS',
    accent: 'text-blue-400',
    ring: 'group-hover:border-blue-500/40',
    color: 'from-blue-500/15 to-transparent',
    title: 'Toda la República Mexicana',
    price: 'Desde $149',
    priceNote: 'Envío gratis en compras +$1,500',
    bullets: [
      'Enviamos con DHL, Estafeta y J&T Express — escogemos el más seguro por zona.',
      'Pedidos realizados antes de las 3 pm salen el mismo día hábil.',
      'Zonas foráneas o extendidas pueden tardar hasta 8 días.',
    ],
  },
];

const RETURNS = [
  {
    icon: PackageCheck,
    title: 'Producto con falla de fábrica',
    text: 'Todos los equipos cuentan con garantía contra defectos de fabricación. Envíanos foto/video del defecto y tu número de pedido por WhatsApp; iniciamos el cambio en menos de 24 h.',
  },
  {
    icon: PackageX,
    title: 'Producto equivocado',
    text: '¿Recibiste algo distinto a lo que pediste? Escríbenos en las primeras 48 h con foto del paquete y del producto; te enviamos el correcto sin costo y recogemos el otro.',
  },
  {
    icon: RotateCcw,
    title: 'Cambio por talla / sabor',
    text: 'Por higiene, los desechables abiertos no tienen cambio. Si tu producto tiene empaque original y sellos intactos, sí podemos realizar el cambio. Te recomendamos elegir bien tu sabor antes de confirmar tu pedido, ya que los cambios pueden tomar algunos días en procesarse.',
  },
  {
    icon: AlertTriangle,
    title: 'Paquete perdido o dañado',
    text: 'La garantía de entrega de NubesMTY te repone el pedido en caso de extravío o daño en el traslado, sin trámites. Excepciones: BCS y Quintana Roo por revisiones aduanales frecuentes.',
  },
];

export default function EnviosPage() {
  const brand = useSiteBrand();
  const siteName = brand?.site_name || 'NubesMTY';

  return (
    <div className="min-h-[calc(100vh-160px)] pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300/90">Envíos y devoluciones</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
            De {siteName} a tu <span className="text-orange-400">puerta</span>
          </h1>
          <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Envíos rápidos y discretos en todo México, con garantía de entrega y proceso simple de devolución si algo sale mal.
          </p>
        </div>
      </section>

      {/* Free shipping banner */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-10">
        <div className="relative overflow-hidden rounded-3xl border border-orange-500/25 bg-gradient-to-r from-orange-500/15 via-orange-500/5 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-4 md:gap-6 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-orange-500 text-black flex items-center justify-center shadow-lg shadow-orange-500/30 shrink-0">
              <Truck className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300">ENVÍO GRATIS</div>
              <h3 className="text-xl md:text-2xl font-black text-white mt-1">
                En todas tus compras
              </h3>
              <p className="text-white/60 text-sm mt-1">Envío gratis en cada pedido. Válido únicamente en Monterrey y área metropolitana.</p>
            </div>
            <Link
              to="/catalogo"
              className="ml-auto inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-black font-bold text-sm hover:bg-white/90 transition"
              data-testid="envios-catalogo-btn"
            >
              Ver catálogo
            </Link>
          </div>
        </div>
      </section>

      {/* Shipping zones */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">

        {/* Carriers strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['DHL Express', 'Estafeta', 'J&T Express', 'Pikpak MTY'].map((c) => (
            <div key={c} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3">
              <Package className="w-5 h-5 text-orange-400" />
              <span className="text-white font-bold text-sm">{c}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Returns */}
      <section className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/25 px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300">Política de devoluciones</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mt-4">
            Compra con <span className="text-orange-400">respaldo total</span>
          </h2>
          <p className="mt-3 text-white/60 text-[15px] leading-relaxed">
            Si algo no salió como esperabas, lo resolvemos. Cuatro escenarios cubiertos con proceso claro y sin letra chiquita.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {RETURNS.map((r) => {
            const Ic = r.icon;
            return (
              <div key={r.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-orange-500/25 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-orange-500/15 text-orange-400 flex items-center justify-center">
                  <Ic className="w-5 h-5" />
                </div>
                <h4 className="text-white font-black text-lg mt-4">{r.title}</h4>
                <p className="text-white/65 text-sm mt-2 leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Help CTA */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 mt-10">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#25D366]/10 to-transparent p-6 md:p-8 flex items-center gap-5 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center shrink-0">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-lg font-black text-white">¿Ya recibiste tu pedido y algo no cuadra?</h3>
            <p className="text-white/60 text-sm mt-1">Escríbenos por WhatsApp con tu número de pedido y en menos de 24 h iniciamos tu proceso.</p>
          </div>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-black font-bold text-sm transition-colors"
            data-testid="envios-contacto-btn"
          >
            Ir a contacto
          </Link>
        </div>
      </section>
    </div>
  );
}
